import './Select.css';

import {
  type ChangeEvent,
  type ComponentProps,
  type ReactNode,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { ChevronDown } from '../Icons';

export interface Option<OptionType extends string | number> {
  value: OptionType;
  label: string;
}

interface SelectProps<
  OptionType extends string | number,
> extends ComponentProps<'select'> {
  label: string;
  options: Option<OptionType>[];
}

export const Select = <OptionType extends string | number>({
  label,
  options,
  onChange,
  ref,
  name = 'select',
  ...props
}: SelectProps<OptionType>): ReactNode => {
  const internalRef = useRef<HTMLSelectElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => internalRef.current!, []);

  const [value, setValue] = useState<OptionType | undefined>(undefined);

  const showPicker = (): void => {
    internalRef.current?.showPicker();
  };

  return (
    <div onClick={showPicker} className="select-container">
      <div className="filled-select-label-container">
        {value !== undefined && <p className="text-xs regular">{label}</p>}
        <label
          htmlFor={name}
          className={`text-base regular ${value === undefined ? '' : 'hide'} select-label`}
        >
          {label}
        </label>
        <select
          ref={internalRef}
          name={name}
          className={`text-base regular select ${value === undefined ? 'hide' : ''}`}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            setValue(event.currentTarget.value as OptionType);
            if (onChange) onChange(event);
          }}
          {...props}
        >
          {/* This hack is needed to have no option selected by default */}
          <option hidden disabled selected value={undefined} />
          {options.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <ChevronDown size={24} />
    </div>
  );
};
